import React, { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { DsAlert, DsButton, DsSpinner } from "../../../../design-system";
import { attendanceService } from "../../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusFromRows,
} from "../../../Common/import-validation";

const TEMPLATE_HEADERS = [
  "Employee Code",
  "Employee Name",
  "LOP Date",
  "New Status",
  "Payroll Month",
  "Reversed Days",
  "Reversed Half",
  "Remarks",
];

const HEADER_MAP = [
  { key: "employeeCode", re: /^(employee\s*code|emp\s*code|code)$/i },
  { key: "employeeName", re: /^(employee\s*name|emp\s*name|name)$/i },
  { key: "lopDate", re: /^(lop\s*date|attendance\s*date|date)$/i },
  { key: "newStatus", re: /^(new\s*status|to\s*status|status)$/i },
  { key: "payrollMonth", re: /^(payroll\s*month|pay\s*month|paymonth)$/i },
  { key: "reversedDays", re: /^(reversed\s*days|days|lop\s*days)$/i },
  { key: "reversedHalf", re: /^(reversed\s*half|half)$/i },
  { key: "remarks", re: /^(remarks|notes|reason|comments)$/i },
];

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase();
}

function cellToText(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof value === "number" && Number.isFinite(value) && value > 20000 && value < 80000) {
    const epoch = Date.UTC(1899, 11, 30) + Math.round(value) * 86400000;
    return new Date(epoch).toISOString().slice(0, 10);
  }
  return value == null ? "" : String(value).trim();
}

function parseSheetToRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheetName =
          workbook.SheetNames.find((n) => /lop/i.test(n) && !/instruction/i.test(n)) ||
          workbook.SheetNames.find((n) => !/instruction/i.test(n)) ||
          workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: true });
        if (!matrix.length) {
          resolve({ rows: [], headers: [] });
          return;
        }
        const headers = (matrix[0] || []).map((h) => String(h || "").trim());
        const colIndex = {};
        headers.forEach((header, idx) => {
          const match = HEADER_MAP.find((m) => m.re.test(normalizeHeader(header)));
          if (match && colIndex[match.key] == null) colIndex[match.key] = idx;
        });
        if (colIndex.employeeCode == null) {
          reject(new Error("Sheet must include an Employee Code column."));
          return;
        }
        if (colIndex.lopDate == null) {
          reject(new Error("Sheet must include an LOP Date column."));
          return;
        }
        if (colIndex.payrollMonth == null) {
          reject(new Error("Sheet must include a Payroll Month column."));
          return;
        }
        const rows = [];
        for (let i = 1; i < matrix.length; i += 1) {
          const cells = matrix[i] || [];
          const blank = cells.every((c) => c == null || String(c).trim() === "");
          if (blank) continue;
          const pick = (key) => {
            const idx = colIndex[key];
            return idx == null ? "" : cells[idx];
          };
          rows.push({
            __row: i + 1,
            employeeCode: cellToText(pick("employeeCode")),
            employeeName: cellToText(pick("employeeName")),
            lopDate: cellToText(pick("lopDate")),
            newStatus: cellToText(pick("newStatus")),
            payrollMonth: cellToText(pick("payrollMonth")),
            reversedDays: cellToText(pick("reversedDays")),
            reversedHalf: cellToText(pick("reversedHalf")),
            remarks: cellToText(pick("remarks")),
          });
        }
        resolve({ rows, headers });
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function LopReversalImportPanel({ darkMode = false }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [imported, setImported] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await attendanceService.downloadLopReversalImportTemplate();
      const blob = res?.data instanceof Blob ? res.data : new Blob([res.data]);
      triggerBlobDownload(blob, "LOP_Reversal_Import_Template.xlsx");
    } catch (err) {
      console.error(err);
      toast.error("Could not download the LOP reversal template.");
    }
  };

  const resetFile = () => {
    setFile(null);
    setReview(null);
    setImported(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validateFile = async () => {
    if (!file) {
      toast.error("Choose an Excel file to validate.");
      return;
    }
    setBusy(true);
    setImported(null);
    try {
      const parsed = await parseSheetToRows(file);
      if (!parsed.rows.length) {
        toast.error("No data rows found in the sheet.");
        return;
      }
      const res = await attendanceService.importLopReversal({
        rows: parsed.rows,
        dryRun: true,
      });
      const data = res?.data || res;
      const built = buildImportValidationResult({
        validRecords: data.validRecords || [],
        invalidRecords: data.invalidRecords || [],
        sourceHeaders: TEMPLATE_HEADERS,
      });
      setReview(built);
      if (built.failedCount === 0) {
        toast.success(`${built.successCount} row(s) ready to import.`);
      } else {
        toast.error(
          `${built.failedCount} row(s) failed validation. Review the list before importing.`
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Validation failed.");
    } finally {
      setBusy(false);
    }
  };

  const commitRows = async (records) => {
    const rows = (records || [])
      .map((r) => {
        const rec = r.record || {};
        return {
          __row: r.row,
          employeeCode: rec.employeeCode,
          employeeName: rec.employeeName,
          lopDate: rec.lopDate || rec.date,
          newStatus: rec.newStatus || rec.status,
          payrollMonth: rec.payrollMonth,
          reversedDays: rec.reversedDays,
          reversedHalf: rec.reversedHalf,
          remarks: rec.remarks,
        };
      })
      .filter((r) => r.employeeCode && r.lopDate && r.payrollMonth);
    if (!rows.length) {
      toast.error("No successful rows to import.");
      return;
    }
    setBusy(true);
    try {
      const res = await attendanceService.importLopReversal({
        rows,
        dryRun: false,
        commit: true,
      });
      const data = res?.data || res;
      if (data?.invalidRecords?.length) {
        const built = buildImportValidationResult({
          validRecords: data.validRecords || [],
          invalidRecords: data.invalidRecords || [],
          sourceHeaders: TEMPLATE_HEADERS,
        });
        setReview(built);
        toast.error("Some rows could not be imported. Review the failed list.");
        return;
      }
      setImported(data.imported || rows.length);
      setReview(null);
      toast.success(`Imported ${data.imported || rows.length} LOP reversal day(s).`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ds-page-panel mx-auto max-w-4xl space-y-4 p-4 lg:p-6">
      <div>
        <h2 className="ds-h3">Import LOP reversal</h2>
        <p className="ds-body mt-1 text-[hsl(var(--ds-muted-fg))]">
          Bulk-save LOP reversals the same way as Actions → LOP Reversal. Each row is one
          reversed attendance date tagged to a payroll month.
        </p>
      </div>

      <DsAlert tone="info">
        LOP month must already be attendance-locked. Payroll month must be unlocked and not
        payroll-finalised. Amount is calculated later in Run Payroll, not in this file.
      </DsAlert>

      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-4">
        <p className="ds-caption font-semibold text-[hsl(var(--ds-fg))]">Template columns</p>
        <ul className="ds-body mt-2 list-disc space-y-1 pl-5 text-[hsl(var(--ds-muted-fg))]">
          <li>Employee Code, LOP Date, New Status, Payroll Month — required</li>
          <li>Reversed Days 1 or 0.5 (default 1). Reversed Half FH/SH when 0.5</li>
          <li>Dates as YYYY-MM-DD or DD/MM/YYYY. Duplicate employee + date + pay month is rejected</li>
        </ul>
      </div>

      {imported != null && (
        <DsAlert tone="success">
          {imported} LOP reversal day(s) saved. They appear under Process Attendance and payroll
          for the tagged pay month.
        </DsAlert>
      )}

      {busy && !review ? (
        <div className="flex items-center gap-2 py-6 text-[hsl(var(--ds-muted-fg))]">
          <DsSpinner />
          <span className="ds-body">Validating file…</span>
        </div>
      ) : review ? (
        <ImportValidationReview
          result={review}
          busy={busy}
          title="LOP reversal validation"
          entityLabel="day"
          onBack={resetFile}
          onDownloadReport={() =>
            downloadValidationStatusFromRows(
              review.allRows,
              "LOP_Reversal_Import_Validation.xlsx"
            )
          }
          onImportAll={() => commitRows(review.successRows)}
          onImportSuccessfulOnly={() => commitRows(review.successRows)}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <DsButton type="button" variant="outline" onClick={downloadTemplate} className="gap-2">
              <Download size={16} />
              Download template
            </DsButton>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const next = e.target.files?.[0] || null;
                setFile(next);
                setImported(null);
              }}
            />
            <DsButton
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={16} />
              Choose file
            </DsButton>
            {file ? (
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">{file.name}</span>
            ) : null}
          </div>
          <DsButton type="button" disabled={!file || busy} onClick={validateFile}>
            Validate &amp; preview
          </DsButton>
        </div>
      )}
      <p className={darkMode ? "sr-only" : "sr-only"}>LOP reversal import</p>
    </div>
  );
}
